import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, AlertCircle, Save } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { ReminderType, ReminderPriority, ReminderCategory, Reminder } from '../../types/reminder';
import { useAuth } from '../../contexts/AuthContext';
import { toDateInputValue } from '../../utils/dateUtils';

interface ReminderEditPageProps {
  petId: string;
  reminderId: string;
}

const ReminderEditPage: React.FC<ReminderEditPageProps> = ({ petId, reminderId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reminder, setReminder] = useState<Reminder | null>(null);
  const [petName, setPetName] = useState<string>('');
  const [reminderTypes, setReminderTypes] = useState<ReminderType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    reminder_type_id: '',
    due_date: '',
    send_time: '09:00',
    priority: ReminderPriority.NORMAL,
    delivery_method: 'EMAIL',
    title: '',
    message: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    if (!petId || !reminderId) {
      setError('Missing reminder identifier');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing authentication token');
      }

      // Fetch reminder
      const reminderResponse = await fetch(API_ENDPOINTS.REMINDERS.GET(reminderId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!reminderResponse.ok) {
        const errorPayload = await reminderResponse.json().catch(() => null);
        const detail = errorPayload?.detail || reminderResponse.statusText || 'Failed to fetch reminder';
        console.error('Reminder fetch failed', reminderResponse.status, detail);
        throw new Error(detail);
      }

      const reminderData = await reminderResponse.json();
      setReminder(reminderData);

      // Fetch pet name
      const petResponse = await fetch(API_ENDPOINTS.PETS.GET(petId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (petResponse.ok) {
        const petData = await petResponse.json();
        setPetName(petData.name);
      }

      // Fetch reminder types
      const typesResponse = await fetch(API_ENDPOINTS.REMINDER_TYPES.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (typesResponse.ok) {
        const typesData = await typesResponse.json();
        setReminderTypes(Array.isArray(typesData) ? typesData : []);
      }

      // Pre-populate form
      // Use toDateInputValue to extract date without UTC conversion issues
      const dueDateStr = toDateInputValue(reminderData.due_date);
      let sendTime = '09:00';
      if (reminderData.scheduled_send_time) {
        const scheduledDate = new Date(reminderData.scheduled_send_time);
        const hours = scheduledDate.getHours().toString().padStart(2, '0');
        const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
        sendTime = `${hours}:${minutes}`;
      }

      setFormData({
        reminder_type_id: reminderData.reminder_type_id || '',
        due_date: dueDateStr,
        send_time: sendTime,
        priority: reminderData.priority || ReminderPriority.NORMAL,
        delivery_method: reminderData.delivery_method || 'EMAIL',
        title: reminderData.title || reminderData.reminder_type?.name || '',
        message: '',
        notes: reminderData.notes || ''
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reminder');
    } finally {
      setLoading(false);
    }
  }, [petId, reminderId]);

  useEffect(() => {
    if (!petId || !reminderId) {
      setError('Missing reminder identifier');
      setLoading(false);
      return;
    }

    fetchData();
  }, [petId, reminderId, fetchData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');

      if (!user?.practice_id) {
        throw new Error('Practice ID not found. Please log in again.');
      }

      const localDateTime = new Date(`${formData.due_date}T${formData.send_time}`);
      const scheduledSendTime = localDateTime.toISOString();

      const response = await fetch(API_ENDPOINTS.REMINDERS.UPDATE(reminderId!), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          due_date: new Date(formData.due_date).toISOString(),
          scheduled_send_time: scheduledSendTime,
          priority: formData.priority,
          notes: formData.notes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update reminder');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/dashboard/pets/${petId}`);
      }, 1500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleReminderTypeChange = (typeId: string) => {
    const selectedType = reminderTypes.find(t => t.id === typeId);
    if (selectedType) {
      setFormData(prev => ({
        ...prev,
        reminder_type_id: typeId,
        title: selectedType.name,
        message: selectedType.description || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        reminder_type_id: typeId
      }));
    }
  };

  const getCategoryColor = (category: ReminderCategory) => {
    const colors = {
      [ReminderCategory.VACCINATION]: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      [ReminderCategory.CHECKUP]: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      [ReminderCategory.MEDICATION]: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      [ReminderCategory.APPOINTMENT]: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      [ReminderCategory.FOLLOW_UP]: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      [ReminderCategory.GROOMING]: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
      [ReminderCategory.DENTAL]: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
      [ReminderCategory.PARASITE_PREVENTION]: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      [ReminderCategory.LAB_WORK]: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
      [ReminderCategory.PRESCRIPTION_REFILL]: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      [ReminderCategory.OTHER]: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    };
    return colors[category] || colors[ReminderCategory.OTHER];
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading reminder...</span>
        </div>
      </div>
    );
  }

  if (!reminder) {
    return (
      <div className="h-full bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary mb-2">Reminder Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The reminder you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 dark:bg-dark-bg-primary overflow-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
              <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
                Edit Reminder
              </h1>
              <p className="text-gray-600 dark:text-dark-text-secondary">
                Update reminder for {petName || 'pet'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Messages */}
          {(error || success) && (
            <div className="mb-6 space-y-4">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
              )}
              {success && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                  <div className="w-5 h-5 text-green-600 dark:text-green-400">✓</div>
                  <p className="text-sm text-green-800 dark:text-green-300">Reminder updated successfully! Redirecting...</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white dark:bg-dark-bg-secondary shadow-sm rounded-lg border border-gray-200 dark:border-dark-border p-6 space-y-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-4">
                  Reminder Details
                </h2>

                {/* Reminder Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Reminder Type *
                  </label>
                  <select
                    value={formData.reminder_type_id}
                    onChange={(e) => handleReminderTypeChange(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a reminder type...</option>
                    {reminderTypes
                      .filter(type => type.is_enabled)
                      .map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({type.category})
                        </option>
                      ))}
                  </select>
                  {reminderTypes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {reminderTypes
                        .filter(type => type.is_enabled)
                        .slice(0, 6)
                        .map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => handleReminderTypeChange(type.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${formData.reminder_type_id === type.id
                                ? 'bg-blue-600 text-white'
                                : getCategoryColor(type.category)
                              }`}
                          >
                            {type.name}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    placeholder="e.g., Annual Rabies Vaccination"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Internal Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    placeholder="Internal notes (not sent to pet owner)..."
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    These notes are for staff only and won't be sent to the pet owner.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white dark:bg-dark-bg-secondary shadow-sm rounded-lg border border-gray-200 dark:border-dark-border p-6 space-y-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-4">
                  Schedule & Settings
                </h2>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Due Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Send Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Send Time *
                  </label>
                  <input
                    type="time"
                    value={formData.send_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, send_time: e.target.value }))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Priority *
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as ReminderPriority }))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={ReminderPriority.LOW}>Low</option>
                    <option value={ReminderPriority.NORMAL}>Normal</option>
                    <option value={ReminderPriority.HIGH}>High</option>
                    <option value={ReminderPriority.URGENT}>Urgent</option>
                  </select>
                </div>

                {/* Delivery Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                    Delivery Method *
                  </label>
                  <select
                    value={formData.delivery_method}
                    onChange={(e) => setFormData(prev => ({ ...prev, delivery_method: e.target.value }))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="EMAIL">📧 Email</option>
                    <option value="EMAIL_HTML">📧✨ HTML Email</option>
                    <option value="SMS">📱 SMS</option>
                  </select>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Choose how the reminder will be delivered.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-8 flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={saving}
              className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.reminder_type_id || !formData.title || !formData.due_date}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReminderEditPage;

